/**
 * Home Page for HMH Lunch Ordering System
 *
 * Displays today's menu options and allows ordering with a floating cart.
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/auth';
import { TodaysMenu, SaladBuilder, DailySpecial, MenuCalendarDropdown } from '../components/menu';
import { useCreateOrder, useCutoffStatus, useMyOrders, useCancelOrder } from '../hooks/useOrders';
import { useTodaysMenu } from '../hooks/useMenu';
import { useMidnightMenu } from '../hooks/useMidnight';
import { useMyTickets } from '../hooks/useTickets';
import { useEmployeeSearch } from '../hooks/useBreakfast';
import { apiConfig } from '../config/msalConfig';
import { useQueryClient } from '@tanstack/react-query';

interface CartItem {
  type: 'regular' | 'salad' | 'special';
  name: string;
  menuItemId?: number;
  specialId?: number;
  toppingIds?: number[];
  toppingNames?: string[];
  optionIds?: number[];
  optionNames?: string[];
}

export function HomePage() {
  const { user, logout, isSsoUser, accessToken } = useAuth();
  const { createOrder, loading: orderLoading, error: orderError } = useCreateOrder();
  const { data: midnightMenu } = useMidnightMenu();
  const { status: cutoffStatus, checkCutoff } = useCutoffStatus();
  const { orders: todaysOrders, fetchOrders, setOrders } = useMyOrders();
  const { cancelOrder } = useCancelOrder();
  const { menu } = useTodaysMenu();  // Uses /menu/today-for-ordering which handles cutoff automatically
  const { data: myTickets } = useMyTickets();
  const queryClient = useQueryClient();
  const availableTicket = myTickets?.find(t => t.status === 'active') ?? null;

  const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);
  const [cart, setCart] = useState<CartItem | null>(null);
  const [specialRequest, setSpecialRequest] = useState('');
  const [useTicket, setUseTicket] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Order-for-someone-else (users with the can_order_for_others permission)
  const [orderForOpen, setOrderForOpen] = useState(false);
  const [orderForQuery, setOrderForQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [orderingFor, setOrderingFor] = useState<{ name: string; number: string | null } | null>(null);
  const { data: employeeSearch, isFetching: searchingEmployees } = useEmployeeSearch(debouncedQuery);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(orderForQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [orderForQuery]);

  useEffect(() => {
    checkCutoff();
  }, [checkCutoff]);
  useEffect(() => {
    if (accessToken && cutoffStatus?.target_date) {
      fetchOrders(accessToken, cutoffStatus.target_date);
    }
  }, [accessToken, cutoffStatus?.target_date, fetchOrders]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const orderingBlocked = user?.orderingBlocked ?? false;
  const isOrderingOpen = (cutoffStatus?.is_open ?? true) && !orderingBlocked;
  const targetDate = cutoffStatus?.target_date;

  const handleRegularOrder = (menuItemId: number, menuItemName: string, optionIds?: number[], optionNames?: string[]) => {
    setCart({
      type: 'regular',
      name: optionNames?.length ? `${menuItemName} — ${optionNames.join(', ')}` : menuItemName,
      menuItemId,
      optionIds,
      optionNames
    });
  };

  const handleSaladOrder = (toppingIds: number[], toppingNames: string[]) => {
    setCart({
      type: 'salad',
      name: `Salad with ${toppingNames.join(', ')}`,
      toppingIds,
      toppingNames
    });
  };

  const handleSpecialOrder = (specialId: number, specialName: string, optionIds?: number[], optionNames?: string[]) => {
    setCart({
      type: 'special',
      name: optionNames?.length ? `${specialName} — ${optionNames.join(', ')}` : specialName,
      specialId,
      optionIds,
      optionNames
    });
  };

  // Double-click guard: React state (orderLoading) updates async, so two
  // rapid clicks could both fire before the button disables. A ref is sync.
  const submittingRef = useRef(false);

  const handleSubmitOrder = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await doSubmitOrder();
    } finally {
      submittingRef.current = false;
    }
  };

  const doSubmitOrder = async () => {
    if (!cart) return;
    // Never fail silently: an expired session or closed window used to make
    // this a no-op while the cart kept showing the item, so people believed
    // their order was placed when nothing was ever sent (CEO, 2026-09-29)
    if (!accessToken) {
      alert('Your session has expired, so this order was NOT submitted. Please refresh the page, sign in again, and re-submit your order.');
      return;
    }
    if (!targetDate) {
      alert('Ordering is closed right now, so this order was NOT submitted. Ordering reopens at 1:00 PM for tomorrow.');
      return;
    }

    // Placing the order on someone else's behalf: goes through the kiosk
    // endpoint so it lands on their account, not the signed-in user's; the
    // token lets it through off-site (the endpoint is hospital-only, HMH-2636)
    if (orderingFor) {
      const item: Record<string, unknown> = {
        order_type: cart.type,
        notes: specialRequest || undefined,
      };
      if (cart.type === 'regular') item.menu_item_id = cart.menuItemId;
      if (cart.type === 'salad') item.topping_ids = cart.toppingIds;
      if (cart.type === 'special') item.special_id = cart.specialId;
      if (cart.optionIds?.length) item.option_ids = cart.optionIds;

      try {
        const resp = await fetch(`${apiConfig.baseUrl}/orders/kiosk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({
            employee_name: orderingFor.name,
            employee_number: orderingFor.number ?? undefined,
            is_guest: false,
            orders: [item],
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          alert(err.detail || 'Failed to place the order. Please try again.');
          return;
        }
        setToastMessage(`Order placed for ${orderingFor.name}`);
        setShowToast(true);
        setCart(null);
        setSpecialRequest('');
        setOrderingFor(null);
        setOrderForQuery('');
      } catch {
        alert('Failed to place the order. Please try again.');
      }
      return;
    }

    let result;
    const ticketCode = useTicket && availableTicket ? availableTicket.ticket_code : undefined;
    const orderData: any = {
      order_date: targetDate,
      notes: specialRequest || undefined,
      ticket_code: ticketCode,
    };

    if (cart.type === 'regular' && cart.menuItemId) {
      result = await createOrder(accessToken, {
        ...orderData,
        order_type: 'regular',
        menu_item_id: cart.menuItemId,
        option_ids: cart.optionIds
      });
    } else if (cart.type === 'salad' && cart.toppingIds) {
      result = await createOrder(accessToken, {
        ...orderData,
        order_type: 'salad',
        topping_ids: cart.toppingIds
      });
    } else if (cart.type === 'special' && cart.specialId) {
      result = await createOrder(accessToken, {
        ...orderData,
        order_type: 'special',
        special_id: cart.specialId,
        option_ids: cart.optionIds
      });
    }

    if (result) {
      const firstName = user?.fullName?.split(' ')[0] || 'there';
      const orderType = cart.type === 'regular' ? 'Lunch' : cart.type === 'salad' ? 'Salad' : 'Special';
      const redeemed = (result as any).ticket_redeemed;
      setToastMessage(
        redeemed
          ? `🎂 Happy Birthday ${firstName}! Your free ${orderType} order is in.`
          : `Thank you ${firstName} for your ${orderType} Order`
      );
      setShowToast(true);
      setCart(null);
      setSpecialRequest('');
      setUseTicket(false);
      if (redeemed) {
        queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
      }
      if (accessToken && targetDate) {
        fetchOrders(accessToken, targetDate);
      }
    }
  };

  const handleClearCart = () => {
    setCart(null);
    setSpecialRequest('');
  };
  const handleCancelOrder = async (orderId: number) => {
    if (!accessToken || !targetDate) return;
    
    if (confirm('Are you sure you want to cancel this order?')) {
      setCancellingOrderId(orderId);
      const success = await cancelOrder(accessToken, orderId);
      
      if (success) {
        setToastMessage('Order cancelled successfully');
        setShowToast(true);
        // Optimistically remove the order from local state
        setOrders(prev => prev.filter(o => o.id !== orderId));
      }
      
      setCancellingOrderId(null);
    }
  };

  const getOrderTypeLabel = (orderType: string) => {
    switch (orderType) {
      case 'regular': return 'Lunch';
      case 'salad': return 'Salad';
      case 'special': return 'Special';
      default: return orderType;
    }
  };

  const getOrderName = (order: any) => {
    if (order.menu_item_name) return order.menu_item_name;
    if (order.special_name) return order.special_name;
    if (order.toppings && order.toppings.length > 0) {
      return `Salad with ${order.toppings.map((t: any) => t.topping_name).join(', ')}`;
    }
    return 'Order';
  };


  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center animate-slide-down">
          <div className="mt-4 bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg font-medium text-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <header className="bg-hmh-blue shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white">Hood Memorial Hospital</h1>
              <p className="text-white/80">Lunch Ordering System</p>
            </div>
            <div className="flex items-center gap-4">
              {midnightMenu?.ordering_status?.enabled && (
                <Link
                  to="/midnight"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-900 rounded-lg hover:bg-indigo-800 transition-colors"
                >
                  🌙 Midnight Special
                </Link>
              )}
              <MenuCalendarDropdown />
              {user?.canOrderForOthers && (
                <button
                  onClick={() => { setOrderForOpen((v) => !v); if (orderingFor) { setOrderingFor(null); setOrderForQuery(''); } }}
                  className="px-4 py-2 text-sm font-medium text-hmh-blue bg-white rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {orderForOpen || orderingFor ? 'Cancel Order for Someone' : 'Order for Someone Else'}
                </button>
              )}
              {user?.canViewDeductions && !user?.isAdmin && (<Link to="/reports/deductions" className="px-4 py-2 text-sm font-medium text-hmh-blue bg-white rounded-lg hover:bg-gray-100 transition-colors">Payroll Report</Link>)}
              {user?.isAdmin && (<Link to="/admin/orders" className="px-4 py-2 text-sm font-medium text-hmh-blue bg-white rounded-lg hover:bg-gray-100 transition-colors">Admin Console</Link>)}
              <div className="text-right">
                <p className="font-medium text-white">{user?.fullName}</p>
                <p className="text-sm text-white/70">{user?.employeeNumber && `#${user.employeeNumber}`}{isSsoUser && ' (SSO)'}</p>
              </div>
              <button onClick={logout} className="px-4 py-2 text-sm font-medium text-hmh-blue bg-white rounded-lg hover:bg-gray-100 transition-colors">Sign Out</button>
            </div>
          </div>
        </div>
      </header>

      {/* Flex container for main content and cart */}
      <div className="flex gap-6 max-w-7xl mx-auto px-4 py-8">
        {/* Main content - takes remaining space */}
        <main className="flex-1">
          {orderError && (<div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800 font-medium">{orderError}</p></div>)}

          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome, {user?.fullName}!</h2>
            <p className="text-gray-600">{isOrderingOpen ? `Select your lunch option. Orders must be placed before 10:00 AM.` : cutoffStatus?.message || 'Ordering is currently closed.'}</p>
          </div>

          {orderingBlocked && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-5 py-4 mb-8 text-amber-900">
              <b>Lunch ordering isn&apos;t available for agency and contract staff.</b>{' '}
              Meals are paid by payroll deduction, which only applies to hospital employees.
              Please see the Front Desk to purchase a meal.
            </div>
          )}

          {/* Order-for-someone-else: employee search */}
          {orderForOpen && !orderingFor && (
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border-2 border-hmh-blue">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Who are you ordering for?</h3>
              <input
                type="text"
                value={orderForQuery}
                onChange={(e) => setOrderForQuery(e.target.value)}
                placeholder="Search by name or employee number..."
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-hmh-blue"
              />
              {searchingEmployees && <p className="text-sm text-gray-500 mt-2">Searching...</p>}
              {employeeSearch && debouncedQuery.length >= 2 && (
                <div className="mt-3 divide-y divide-gray-100 max-h-72 overflow-y-auto">
                  {employeeSearch.results.length === 0 && !searchingEmployees && (
                    <p className="text-sm text-gray-500 py-2">No employees found.</p>
                  )}
                  {employeeSearch.results.map((emp) => (
                    <button
                      key={`${emp.display_name}-${emp.employee_number}`}
                      onClick={() => { setOrderingFor({ name: emp.display_name, number: emp.employee_number }); setOrderForOpen(false); }}
                      className="w-full text-left px-3 py-3 hover:bg-blue-50 rounded-lg transition-colors flex justify-between items-center"
                    >
                      <span className="font-medium text-gray-900">{emp.display_name}</span>
                      <span className="text-sm text-gray-500">{emp.employee_number ? `#${emp.employee_number}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Order-for-someone-else: active banner */}
          {orderingFor && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 mb-8 flex items-center justify-between">
              <p className="text-amber-900 font-medium">
                🛒 Ordering for <span className="font-bold">{orderingFor.name}</span>
                {orderingFor.number && ` (#${orderingFor.number})`} — the next order you place goes on their account.
              </p>
              <button
                onClick={() => { setOrderingFor(null); setOrderForQuery(''); }}
                className="px-3 py-2 text-sm font-medium text-amber-900 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors"
              >
                Switch back to me
              </button>
            </div>
          )}

          {/* Your Orders Today Banner - Only show if there are orders */}
          {todaysOrders && todaysOrders.length > 0 && (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Orders Today</h3>
              <div className="space-y-3">
                {todaysOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">
                        {order.order_type === 'regular' ? '🍽️' : order.order_type === 'salad' ? '🥗' : '⭐'}
                      </span>
                      <div>
                        <span className="font-semibold text-gray-900">
                          {getOrderTypeLabel(order.order_type)}
                        </span>
                        <span className="text-gray-600"> - {getOrderName(order)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={cancellingOrderId === order.id}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancellingOrderId === order.id ? 'Cancelling...' : 'Cancel'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 className="text-lg font-semibold text-gray-900 mb-4">{menu?.day_name}'s Menu Options</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch items-stretch">
            <TodaysMenu onOrder={handleRegularOrder} disabled={!isOrderingOpen || orderLoading} />
            {menu?.salad_available && (
              <SaladBuilder onOrder={handleSaladOrder} disabled={!isOrderingOpen || orderLoading} />
            )}
            <DailySpecial onOrder={handleSpecialOrder} disabled={!isOrderingOpen || orderLoading} />
          </div>

          <div className="mt-8 text-center">
            <Link to="/order-history" className="text-blue-600 hover:text-blue-800 font-medium">View Order History →</Link>
          </div>
        </main>

        {/* Cart - fixed width, sticky */}
        <div className="w-80 flex-shrink-0">
          <div className="sticky top-24 bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-hmh-blue text-white p-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Your Order
              </h2>
            </div>

            <div className="p-4 min-h-[400px] max-h-[calc(100vh-200px)] overflow-y-auto">
              {cart ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="inline-block px-2 py-1 bg-hmh-blue text-white text-xs rounded font-medium mb-1">
                          {cart.type === 'regular' ? 'LUNCH' : cart.type === 'salad' ? 'SALAD' : 'SPECIAL'}
                        </span>
                        <p className="font-medium text-gray-900">{cart.name}</p>
                      </div>
                      <button
                        onClick={handleClearCart}
                        className="text-red-500 hover:text-red-700"
                        title="Remove from cart"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Special Request / Dietary Information
                    </label>
                    <textarea
                      value={specialRequest}
                      onChange={(e) => setSpecialRequest(e.target.value)}
                      placeholder="Any special requests or dietary needs?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hmh-blue focus:border-transparent resize-none"
                      rows={4}
                    />
                  </div>

                  {availableTicket && !orderingFor && (
                    <label
                      className={`block rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                        useTicket
                          ? 'bg-amber-50 border-amber-400'
                          : 'bg-amber-50/40 border-amber-200 hover:bg-amber-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={useTicket}
                          onChange={(e) => setUseTicket(e.target.checked)}
                          className="mt-1 h-5 w-5 text-amber-600 rounded focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">🎂</span>
                            <span className="font-semibold text-amber-900">
                              Happy Birthday! Use your free meal ticket
                            </span>
                          </div>
                          <p className="text-xs text-amber-700 mt-1 font-mono">
                            {availableTicket.ticket_code}
                          </p>
                        </div>
                      </div>
                    </label>
                  )}

                  <button
                    onClick={handleSubmitOrder}
                    disabled={orderLoading}
                    className="w-full bg-hmh-blue text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {orderLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {orderingFor ? `Submit for ${orderingFor.name.split(' ')[0]}` : 'Submit Order'}
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-gray-500 font-medium">Your cart is empty</p>
                  <p className="text-sm text-gray-400 mt-1">Add an item from the menu</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 text-center space-y-2">
          <p className="text-xs text-gray-700">By placing this order, you voluntarily authorize payroll deduction of the meal(s) selected.</p>
          <p className="text-sm text-gray-500">Hood Memorial Hospital - Amite, Louisiana</p>
        </div>
      </footer>
    </div>
  );
}
